import type { FC } from 'react';
import React, { memo, useMemo } from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { SsrLazyScreen } from '@apps/guest/packages/layout-engine/components/SsrLazyScreen';
import type { ArchPhotoProps, PhotoArchProps } from './PhotoArch.types';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { OptimizedEventGuestSitePhoto, LOW_RES_RENDITION } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto';
import { addRendition } from '@shared/utils/photoRendition';

const MAX_HEIGHT = 600;
const ASPECT_RATIO = 9 / 7;
const OUTER_PIECE_THICKNESS = 2.88;
const INNER_GAP_THICKNESS_Y = 23;
const INNER_GAP_THICKNESS_X = 17;

/**
 * Decorative arch border overlays. Rendered on top of the image content so
 * the AnimatedImageBox content area matches the full container dimensions
 * (required for CSS crop parity with canvas).
 */
const ArchBorderOverlay: FC<{
  outerArchColor: ArchPhotoProps['outerArchColor'];
  innerArchColor: ArchPhotoProps['innerArchColor'];
  borderRadius: number;
}> = ({ outerArchColor, innerArchColor, borderRadius }) => (
  <>
    <Box
      position="absolute"
      inset={0}
      border={`${pxToRem(OUTER_PIECE_THICKNESS)} solid`}
      borderColor={outerArchColor}
      borderTopLeftRadius={pxToRem(borderRadius)}
      borderTopRightRadius={pxToRem(borderRadius)}
      pointerEvents="none"
      zIndex={1}
    />
    <Box
      position="absolute"
      inset={0}
      backgroundColor="transparent"
      borderTop={pxToRem(INNER_GAP_THICKNESS_X + OUTER_PIECE_THICKNESS)}
      borderRight={pxToRem(INNER_GAP_THICKNESS_Y + OUTER_PIECE_THICKNESS)}
      borderBottom={pxToRem(INNER_GAP_THICKNESS_X + OUTER_PIECE_THICKNESS)}
      borderLeft={pxToRem(INNER_GAP_THICKNESS_Y + OUTER_PIECE_THICKNESS)}
      borderStyle="solid"
      borderColor={innerArchColor}
      borderTopLeftRadius={pxToRem(borderRadius)}
      borderTopRightRadius={pxToRem(borderRadius)}
      pointerEvents="none"
    />
  </>
);

const ArchPhoto: FC<ArchPhotoProps> = props => {
  const { photo, containerDimensions, calculateBorderRadius, outerArchColor, innerArchColor } = props;
  const height = containerDimensions.height;
  const width = height * ASPECT_RATIO;

  return (
    <Box
      display="flex"
      position="relative"
      width="auto"
      height="100%"
      borderTopLeftRadius={pxToRem(calculateBorderRadius)}
      borderTopRightRadius={pxToRem(calculateBorderRadius)}
      aspectRatio={`${ASPECT_RATIO}`}
      overflow="hidden"
    >
      <OptimizedEventGuestSitePhoto
        photoUrl={photo.url}
        cropInfo={photo.cropInfo ? { ...photo.cropInfo, unit: '%' } : null}
        width={width}
        height={height}
        containerType="fixed-aspect"
      />
      <ArchBorderOverlay
        outerArchColor={outerArchColor}
        innerArchColor={innerArchColor}
        borderRadius={calculateBorderRadius}
      />
    </Box>
  );
};

export const PhotoArch: FC<PhotoArchProps> = memo(props => {
  const { photo, showSkeleton, innerArchColor = 'white', outerArchColor = 'mono14', showPlaceholder = false } = props;

  const [containerDimensions, containerRef] = useSizeObserver();

  const calculateBorderRadius = useMemo(() => {
    const maxHeight = photo && containerDimensions.height ? Math.min(MAX_HEIGHT, containerDimensions.height) : MAX_HEIGHT;
    const width = maxHeight * ASPECT_RATIO;
    return width / 2 + OUTER_PIECE_THICKNESS;
  }, [containerDimensions.height, photo]);

  return (
    <Flex
      width="100%"
      height="100%"
      maxHeight={MAX_HEIGHT}
      aspectRatio={`${ASPECT_RATIO}`}
      alignItems="center"
      justifyContent="center"
      ref={containerRef}
    >
      {!photo ? (
        showPlaceholder ? (
          <Box
            width="80%"
            height="100%"
            backgroundColor="lightgray"
            borderTopLeftRadius={pxToRem(calculateBorderRadius)}
            borderTopRightRadius={pxToRem(calculateBorderRadius)}
          />
        ) : null
      ) : (
        <SsrLazyScreen
          key={photo.id}
          src={addRendition({ url: photo.url, renditionSize: LOW_RES_RENDITION })}
          skeletonGroupOverrides={{
            Root: {
              props: {
                width: 'auto',
                height: '100%',
                maxHeight: MAX_HEIGHT,
                aspectRatio: `${ASPECT_RATIO}`,
              },
            },
            PlaceholderRoot: {
              props: {
                width: 'auto',
                height: '100%',
                maxHeight: MAX_HEIGHT,
                aspectRatio: `${ASPECT_RATIO}`,
              },
            },
          }}
          width="100%"
          height="100%"
          aspectRatio={`${ASPECT_RATIO}`}
          showSkeleton={showSkeleton}
        >
          {() => (
            <ArchPhoto
              photo={photo}
              containerDimensions={containerDimensions}
              calculateBorderRadius={calculateBorderRadius}
              outerArchColor={outerArchColor}
              innerArchColor={innerArchColor}
            />
          )}
        </SsrLazyScreen>
      )}
    </Flex>
  );
});

PhotoArch.displayName = 'PhotoArch';
